import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { projectsAPI, projectStagesAPI, projectBoardsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Search, FolderKanban, List, Loader2, Calendar, CheckCircle2, ChevronDown, Pencil, Trash2, X, Check, LayoutGrid, DollarSign, User, MapPin, Phone, Mail, Tag, AlertCircle, FileText, Clock, BarChart3, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { ListPagination } from '../components/ListPagination';
import { CostBreakdownBar } from '../components/CostBreakdownBar';
import { CustomerCombobox } from '../components/CustomerCombobox';
import { CardLayoutConfig } from '../components/CardLayoutConfig';

// ---------------------------------------------------------------------------
// Card Field Registry — defines all fields available for kanban card layout
// ---------------------------------------------------------------------------
export const CARD_FIELD_REGISTRY = {
  job_name:          { label: 'Project Name',      description: 'Name of the project',           group: 'core' },
  job_number:        { label: 'Project #',          description: 'Auto-generated project number', group: 'core' },
  customer_name:     { label: 'Customer Name',      description: 'Customer linked to the project', group: 'customer' },
  customer_phone:    { label: 'Phone',              description: 'Customer phone number',         group: 'customer' },
  customer_email:    { label: 'Email',              description: 'Customer email address',        group: 'customer' },
  customer_address:  { label: 'Address',            description: 'Customer service address',      group: 'customer' },
  contract_value:    { label: 'Contract Value',     description: 'Total contract/sell price',     group: 'financial' },
  budget_total_cost: { label: 'Budget Total',       description: 'Total budgeted cost',           group: 'financial' },
  cost_breakdown:    { label: 'Budget Breakdown',   description: 'Cost type breakdown bar',       group: 'financial' },
  priority:          { label: 'Priority',           description: 'Project priority level',        group: 'status' },
  start_date:        { label: 'Start Date',         description: 'Project start date',            group: 'dates' },
  target_end_date:   { label: 'Target End Date',    description: 'Project target completion',     group: 'dates' },
  tasks:             { label: 'Tasks Progress',     description: 'Done/total task count',         group: 'progress' },
  description:       { label: 'Description',        description: 'Project description snippet',   group: 'details' },
  job_type:          { label: 'Job Type',           description: 'Type/category of project',      group: 'details' },
  lead_source:       { label: 'Lead Source',        description: 'Where the lead originated',     group: 'details' },
  tags:              { label: 'Tags',               description: 'Project tags',                  group: 'details' },
  created_at:        { label: 'Created Date',       description: 'When the project was created',  group: 'dates' },
};

export const DEFAULT_VISIBLE = ['job_name', 'job_number', 'customer_name', 'contract_value', 'tasks', 'start_date', 'cost_breakdown'];

const PRIORITY_STYLES = {
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  high:   'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  normal: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
  low:    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
};

/** Render a single card field. Returns null if the field has no data. */
function CardField({ fieldKey, project }) {
  switch (fieldKey) {
    case 'job_name':
      return <p className="font-semibold text-[13px] text-foreground leading-snug line-clamp-2">{project.job_name}</p>;
    case 'job_number':
      return project.job_number ? <p className="text-[11px] text-muted-foreground">{project.job_number}</p> : null;
    case 'customer_name':
      return project.customer_name ? <p className="text-xs text-muted-foreground truncate flex items-center gap-1"><User className="w-3 h-3 shrink-0" />{project.customer_name}</p> : null;
    case 'customer_phone':
      return project.customer_phone ? <p className="text-xs text-muted-foreground truncate flex items-center gap-1"><Phone className="w-3 h-3 shrink-0" />{project.customer_phone}</p> : null;
    case 'customer_email':
      return project.customer_email ? <p className="text-xs text-muted-foreground truncate flex items-center gap-1"><Mail className="w-3 h-3 shrink-0" />{project.customer_email}</p> : null;
    case 'customer_address':
      return project.customer_address ? <p className="text-xs text-muted-foreground truncate flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" />{project.customer_address}</p> : null;
    case 'contract_value':
      return project.contract_value != null ? (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300 px-1.5 py-0.5 rounded">
          <DollarSign className="w-3 h-3" />{formatCurrency(project.contract_value)}
        </span>
      ) : null;
    case 'budget_total_cost':
      return project.budget_total_cost > 0 ? (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><BarChart3 className="w-3 h-3" />Budget: {formatCurrency(project.budget_total_cost)}</span>
      ) : null;
    case 'cost_breakdown':
      return <CostBreakdownBar breakdown={project.cost_breakdown} variant="card" />;
    case 'priority':
      return project.priority && project.priority !== 'normal' ? (
        <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${PRIORITY_STYLES[project.priority] || PRIORITY_STYLES.normal}`}>
          <AlertCircle className="w-3 h-3" />{project.priority}
        </span>
      ) : null;
    case 'start_date':
      return project.start_date ? <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><Calendar className="w-3 h-3" />{project.start_date}</span> : null;
    case 'target_end_date':
      return project.target_end_date ? <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><Clock className="w-3 h-3" />Due: {project.target_end_date}</span> : null;
    case 'tasks':
      return project.task_count > 0 ? <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><CheckCircle2 className="w-3 h-3" />{project.task_done}/{project.task_count}</span> : null;
    case 'description':
      return project.description ? <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5"><FileText className="w-3 h-3 inline mr-1" />{project.description}</p> : null;
    case 'job_type':
      return project.job_type ? <span className="inline-flex text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{project.job_type}</span> : null;
    case 'lead_source':
      return project.lead_source ? <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground"><Tag className="w-3 h-3" />{project.lead_source}</span> : null;
    case 'tags':
      return project.tags ? (
        <div className="flex flex-wrap gap-1">{project.tags.split(',').map((t, i) => <span key={i} className="text-[10px] bg-muted px-1.5 py-0.5 rounded">{t.trim()}</span>)}</div>
      ) : null;
    case 'created_at':
      return project.created_at ? <span className="text-[10px] text-muted-foreground">{new Date(project.created_at).toLocaleDateString()}</span> : null;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Hardcoded fallbacks (only used until API stages load)
// ---------------------------------------------------------------------------
const FALLBACK_STAGE_CONFIG = {
  lead:        { label: 'Lead', color: '#94a3b8' },
  proposal:    { label: 'Proposal', color: '#3b82f6' },
  awarded:     { label: 'Awarded', color: '#10b981' },
  in_progress: { label: 'In Progress', color: '#f59e0b' },
  punch_list:  { label: 'Punch List', color: '#f97316' },
  completed:   { label: 'Completed', color: '#22c55e' },
  closed:      { label: 'Closed', color: '#6b7280' },
};

function buildStageConfig(stages) {
  const config = {};
  stages.forEach(s => {
    config[s.slug] = { label: s.name, color: s.color, id: s.id };
  });
  return config;
}

function StageBadge({ stage, stageConfig }) {
  const cfg = stageConfig?.[stage] || FALLBACK_STAGE_CONFIG[stage] || { label: stage, color: '#94a3b8' };
  const bgColor = cfg.color + '20';
  return <Badge variant="secondary" className="text-xs font-medium" style={{ backgroundColor: bgColor, color: cfg.color, borderColor: cfg.color + '40' }}>{cfg.label}</Badge>;
}

function formatCurrency(val) {
  if (val == null) return '\u2014';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
}

const PRESET_COLORS = ['#94a3b8', '#3b82f6', '#10b981', '#f59e0b', '#f97316', '#22c55e', '#6b7280', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#0ea5e9'];

// ---------------------------------------------------------------------------
// Board Selector Dropdown
// ---------------------------------------------------------------------------
function BoardSelector({ boards, selectedBoardId, onSelectBoard, onCreateBoard, onRenameBoard, onDeleteBoard, canEdit }) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // board to confirm delete
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef(null);
  const renameRef = useRef(null);

  const selectedBoard = boards.find(b => b.id === selectedBoardId);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await onCreateBoard(newName.trim());
      setNewName('');
      setCreating(false);
    } finally { setSaving(false); }
  };

  const handleRename = async (boardId) => {
    if (!renameName.trim()) return;
    setSaving(true);
    try {
      await onRenameBoard(boardId, renameName.trim());
      setRenaming(null);
      setRenameName('');
    } finally { setSaving(false); }
  };

  if (creating) {
    return (
      <div className="flex items-center gap-2" data-testid="board-create-inline">
        <Input
          ref={inputRef}
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder="Board name..."
          className="h-8 w-48 text-sm"
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setCreating(false); setNewName(''); } }}
          data-testid="new-board-name-input"
        />
        <Button size="sm" className="h-8 px-3 text-xs" onClick={handleCreate} disabled={saving || !newName.trim()} data-testid="confirm-create-board">Create</Button>
        <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => { setCreating(false); setNewName(''); }}>Cancel</Button>
      </div>
    );
  }

  if (renaming) {
    return (
      <div className="flex items-center gap-2" data-testid="board-rename-inline">
        <Input
          ref={renameRef}
          value={renameName}
          onChange={e => setRenameName(e.target.value)}
          className="h-8 w-48 text-sm"
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') handleRename(renaming); if (e.key === 'Escape') { setRenaming(null); setRenameName(''); } }}
          data-testid="rename-board-input"
        />
        <Button size="sm" className="h-8 px-3 text-xs" onClick={() => handleRename(renaming)} disabled={saving || !renameName.trim()} data-testid="confirm-rename-board">Save</Button>
        <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => { setRenaming(null); setRenameName(''); }}>Cancel</Button>
      </div>
    );
  }

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border bg-background hover:bg-muted text-sm font-medium transition-colors" data-testid="board-selector-trigger">
          <LayoutGrid className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="truncate max-w-[180px]">{selectedBoard?.name || 'Select Board'}</span>
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64" data-testid="board-selector-menu">
        {boards.map(b => (
          <DropdownMenuItem
            key={b.id}
            className={`flex items-center justify-between ${b.id === selectedBoardId ? 'bg-muted font-semibold' : ''}`}
            onSelect={(e) => {
              // Prevent closing when clicking edit
              if (e.defaultPrevented) return;
              onSelectBoard(b.id);
            }}
            data-testid={`board-option-${b.id}`}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="truncate">{b.name}</span>
              <span className="text-xs text-muted-foreground shrink-0">{b.project_count}p / {b.stage_count}s</span>
            </div>
            {canEdit && !b.is_default && (
              <div className="flex items-center gap-0.5 ml-1 shrink-0">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setRenaming(b.id);
                    setRenameName(b.name);
                  }}
                  className="p-0.5 text-muted-foreground hover:text-foreground"
                  data-testid={`rename-board-btn-${b.id}`}
                  title="Rename board"
                >
                  <Pencil className="w-3 h-3" />
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDeleteTarget(b);
                    setDeleteError('');
                  }}
                  className="p-0.5 text-muted-foreground hover:text-destructive"
                  data-testid={`delete-board-btn-${b.id}`}
                  title="Delete board"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            )}
            {canEdit && b.is_default && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setRenaming(b.id);
                  setRenameName(b.name);
                }}
                className="p-0.5 text-muted-foreground hover:text-foreground ml-1 shrink-0"
                data-testid={`rename-board-btn-${b.id}`}
                title="Rename board"
              >
                <Pencil className="w-3 h-3" />
              </button>
            )}
          </DropdownMenuItem>
        ))}
        {canEdit && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => setCreating(true)}
              data-testid="create-board-menu-item"
            >
              <Plus className="w-4 h-4 mr-2" /> New Board
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>

    {/* Delete Board Confirmation Dialog */}
    <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteError(''); } }}>
      <AlertDialogContent data-testid="delete-board-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently remove this board, its stages, and card layout configuration. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {deleteTarget?.project_count > 0 && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 text-sm text-destructive" data-testid="delete-board-blocked-msg">
            This board has <strong>{deleteTarget.project_count} project{deleteTarget.project_count !== 1 ? 's' : ''}</strong> assigned. Move or delete those projects before deleting the board.
          </div>
        )}
        {deleteError && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 text-sm text-destructive" data-testid="delete-board-error">
            {deleteError}
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="cancel-delete-board">Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={deleteTarget?.project_count > 0 || deleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={async (e) => {
              e.preventDefault();
              setDeleting(true);
              try {
                await onDeleteBoard(deleteTarget.id);
                setDeleteTarget(null);
              } catch (err) {
                setDeleteError(err?.response?.data?.detail || 'Failed to delete board');
              } finally { setDeleting(false); }
            }}
            data-testid="confirm-delete-board"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Delete Board
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Kanban Board
// ---------------------------------------------------------------------------
function KanbanBoard({ grouped, stages, stageConfig, stageFilter, navigate, canEdit, onStagesChanged, boardId, cardLayout, onOpenCardConfig }) {
  const scrollRef = useRef(null);
  const [editingStageId, setEditingStageId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [savingStage, setSavingStage] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [moveToStageId, setMoveToStageId] = useState('');

  const filteredStages = stageFilter
    ? stages.filter(s => s.slug === stageFilter)
    : stages;

  const handleRenameStage = async (stageId) => {
    if (!editName.trim()) return;
    setSavingStage(true);
    try {
      await projectStagesAPI.update(stageId, { name: editName.trim(), color: editColor });
      setEditingStageId(null);
      onStagesChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rename stage');
    } finally { setSavingStage(false); }
  };

  const handleDeleteStage = async (stageId) => {
    setSavingStage(true);
    try {
      await projectStagesAPI.delete(stageId, moveToStageId || undefined);
      setDeleteConfirm(null); setMoveToStageId('');
      onStagesChanged();
      toast.success('Stage deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete stage');
    } finally { setSavingStage(false); }
  };

  return (
    <div className="relative" data-testid="projects-kanban-wrapper">
      <div ref={scrollRef} className="flex gap-4 overflow-x-auto pb-4 px-1 scrollbar-thin" data-testid="projects-kanban" style={{ scrollbarWidth: 'thin' }}>
        {filteredStages.map(stage => {
          const cards = grouped[stage.slug] || [];
          const isEditing = editingStageId === stage.id;
          const isDeleting = deleteConfirm === stage.id;

          return (
            <div key={stage.slug} className="flex flex-col flex-shrink-0 w-[280px] rounded-xl border border-border/60 bg-background shadow-sm" data-testid={`project-stage-${stage.slug}`}>
              {/* Column header */}
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-t-xl border-b bg-muted/40" style={{ borderTop: `3px solid ${stage.color}` }}>
                {isEditing ? (
                  <div className="flex items-center gap-1.5 flex-1 min-w-0" onClick={e => e.stopPropagation()}>
                    <Input value={editName} onChange={e => setEditName(e.target.value)} className="h-6 text-sm font-semibold flex-1" autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') handleRenameStage(stage.id); if (e.key === 'Escape') setEditingStageId(null); }}
                      data-testid="edit-stage-name-input" />
                    <div className="flex gap-0.5">
                      {PRESET_COLORS.slice(0, 6).map(c => (
                        <button key={c} onClick={() => setEditColor(c)} className={`w-4 h-4 rounded-full border-2 ${editColor === c ? 'border-foreground' : 'border-transparent'}`} style={{ backgroundColor: c }} />
                      ))}
                    </div>
                    <button onClick={() => handleRenameStage(stage.id)} disabled={savingStage} className="text-green-600 hover:text-green-700" data-testid="save-stage-name"><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditingStageId(null)} className="text-muted-foreground"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : isDeleting ? (
                  <div className="flex-1 space-y-1.5" onClick={e => e.stopPropagation()}>
                    <p className="text-xs text-red-600 font-medium">Delete "{stage.name}"?</p>
                    {cards.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-[10px] text-muted-foreground">{cards.length} project(s) need a new stage:</p>
                        <Select value={moveToStageId} onValueChange={setMoveToStageId}>
                          <SelectTrigger className="h-6 text-xs" data-testid="move-projects-select"><SelectValue placeholder="Move to..." /></SelectTrigger>
                          <SelectContent>
                            {stages.filter(s => s.id !== stage.id).map(s => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="flex gap-1">
                      <Button size="sm" variant="destructive" className="h-5 text-[10px] px-2" disabled={savingStage || (cards.length > 0 && !moveToStageId)}
                        onClick={() => handleDeleteStage(stage.id)} data-testid="confirm-delete-stage">Delete</Button>
                      <Button size="sm" variant="ghost" className="h-5 text-[10px] px-2" onClick={() => { setDeleteConfirm(null); setMoveToStageId(''); }}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                    <h3 className="text-sm font-semibold text-foreground truncate flex-1">{stage.name}</h3>
                    <span className="text-xs text-muted-foreground font-medium bg-muted rounded-full px-2 py-0.5">{cards.length}</span>
                    {canEdit && (
                      <div className="flex gap-0.5 shrink-0">
                        <button
                          onClick={e => { e.stopPropagation(); setEditingStageId(stage.id); setEditName(stage.name); setEditColor(stage.color); }}
                          className="p-1 text-muted-foreground/60 hover:text-foreground rounded transition-colors"
                          data-testid={`edit-stage-${stage.slug}`}
                          title="Rename stage"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); setDeleteConfirm(stage.id); }}
                          className="p-1 text-muted-foreground/60 hover:text-red-500 rounded transition-colors"
                          data-testid={`delete-stage-${stage.slug}`}
                          title="Delete stage"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
              {/* Cards area */}
              <div className="flex-1 p-2 space-y-2 min-h-[100px] overflow-y-auto bg-muted/15 rounded-b-xl" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                {cards.map(p => {
                  const visibleFields = cardLayout?.visible_fields || DEFAULT_VISIBLE;
                  const fieldOrder = cardLayout?.field_order || visibleFields;
                  // Render fields in order, only those that are visible
                  const visibleSet = new Set(visibleFields);
                  const orderedFields = fieldOrder.filter(k => visibleSet.has(k));

                  return (
                    <div key={p.id} onClick={() => navigate(`/jobs/projects/${p.id}`)} className="bg-background border border-border/80 rounded-lg p-3 cursor-pointer shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5" data-testid={`project-card-${p.id}`}>
                      {orderedFields.map(fieldKey => (
                        <CardField key={fieldKey} fieldKey={fieldKey} project={p} />
                      ))}
                    </div>
                  );
                })}
                {cards.length === 0 && (
                  <div className="flex items-center justify-center h-16 text-xs text-muted-foreground/50">No projects</div>
                )}
              </div>
            </div>
          );
        })}
        <div className="flex-shrink-0 w-1" aria-hidden="true" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export const ProjectsList = forwardRef(function ProjectsList(props, ref) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOwner, isOffice } = useAuth();
  const canEdit = isOwner || isOffice;

  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('board');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stages, setStages] = useState([]);
  const [stageConfig, setStageConfig] = useState({});

  // Board state
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState(() => searchParams.get('board') || '');
  const [boardsLoading, setBoardsLoading] = useState(true);
  const [cardLayoutOpen, setCardLayoutOpen] = useState(false);

  // Add stage inline state
  const [addingStage, setAddingStage] = useState(false);
  const [newStageName, setNewStageName] = useState('');
  const [newStageColor, setNewStageColor] = useState('#94a3b8');
  const [savingNewStage, setSavingNewStage] = useState(false);

  const search = searchParams.get('psearch') || '';
  const stageFilter = searchParams.get('pstage') || '';
  const page = parseInt(searchParams.get('ppage') || '1', 10);
  const pageSize = 50;

  const [form, setForm] = useState({
    job_name: '', customer_id: '', description: '', project_stage: '',
    contract_value: '', start_date: '', target_end_date: '', priority: 'normal',
    board_id: '',
  });

  // Stages available in the form's selected board
  const [formStages, setFormStages] = useState([]);

  // Expose actions to parent (Jobs.jsx) for FAB menu
  useImperativeHandle(ref, () => ({
    openNewProject: () => setDrawerOpen(true),
    startAddStage: () => setAddingStage(true),
    openCardLayout: () => setCardLayoutOpen(true),
  }));

  // ---- Fetch boards ----
  const fetchBoards = useCallback(async () => {
    setBoardsLoading(true);
    try {
      const res = await projectBoardsAPI.list();
      const boardList = res.data || [];
      setBoards(boardList);
      // Auto-select: use URL param, or previously selected, or default board
      setSelectedBoardId(prev => {
        const urlBoard = searchParams.get('board');
        if (urlBoard && boardList.find(b => b.id === urlBoard)) return urlBoard;
        if (prev && boardList.find(b => b.id === prev)) return prev;
        const def = boardList.find(b => b.is_default);
        return def?.id || boardList[0]?.id || '';
      });
    } catch {
      toast.error('Failed to load boards');
    } finally { setBoardsLoading(false); }
  }, []);

  useEffect(() => { fetchBoards(); }, [fetchBoards]);

  // ---- Persist selected board in URL ----
  const handleSelectBoard = useCallback((boardId) => {
    setSelectedBoardId(boardId);
    const p = new URLSearchParams(searchParams);
    p.set('board', boardId);
    p.delete('pstage');
    p.set('ppage', '1');
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  // ---- Fetch stages for selected board ----
  const fetchStages = useCallback(async () => {
    if (!selectedBoardId) return;
    try {
      const res = await projectStagesAPI.list({ board_id: selectedBoardId });
      const s = res.data.stages || [];
      setStages(s);
      setStageConfig(buildStageConfig(s));
    } catch {
      const fallbackStages = Object.entries(FALLBACK_STAGE_CONFIG).map(([slug, cfg], i) => ({
        id: slug, slug, name: cfg.label, color: cfg.color, sort_order: i, is_archived: false,
      }));
      setStages(fallbackStages);
      setStageConfig(buildStageConfig(fallbackStages));
    }
  }, [selectedBoardId]);

  // ---- Fetch projects for selected board ----
  const fetchProjects = useCallback(async () => {
    if (!selectedBoardId) return;
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, board_id: selectedBoardId };
      if (search) params.search = search;
      if (stageFilter) params.stage = stageFilter;
      const res = await projectsAPI.list(params);
      setProjects(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load projects');
    } finally { setLoading(false); }
  }, [page, search, stageFilter, selectedBoardId]);

  useEffect(() => { fetchStages(); }, [fetchStages]);
  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // When drawer opens, preselect current board in form
  useEffect(() => {
    if (drawerOpen && selectedBoardId) {
      setForm(f => ({ ...f, board_id: f.board_id || selectedBoardId }));
    }
  }, [drawerOpen, selectedBoardId]);

  // Fetch stages for the form's selected board (may differ from the kanban board)
  useEffect(() => {
    if (!form.board_id) { setFormStages([]); return; }
    // If form board matches the kanban board, reuse existing stages
    if (form.board_id === selectedBoardId) {
      setFormStages(stages);
      setForm(f => ({ ...f, project_stage: f.project_stage || (stages[0]?.slug || '') }));
      return;
    }
    // Otherwise fetch from API
    projectStagesAPI.list({ board_id: form.board_id }).then(res => {
      const s = res.data.stages || [];
      setFormStages(s);
      setForm(f => ({ ...f, project_stage: s[0]?.slug || '' }));
    }).catch(() => setFormStages([]));
  }, [form.board_id, selectedBoardId, stages]);

  const handleSearch = (val) => {
    const p = new URLSearchParams(searchParams);
    val ? p.set('psearch', val) : p.delete('psearch');
    p.set('ppage', '1');
    setSearchParams(p, { replace: true });
  };

  const handleStageFilter = (val) => {
    const p = new URLSearchParams(searchParams);
    val && val !== 'all' ? p.set('pstage', val) : p.delete('pstage');
    p.set('ppage', '1');
    setSearchParams(p, { replace: true });
  };

  const handleCreate = async () => {
    if (!form.job_name.trim()) { toast.error('Project name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        job_name: form.job_name.trim(),
        customer_id: form.customer_id || undefined,
        description: form.description || undefined,
        project_stage: form.project_stage,
        contract_value: form.contract_value ? parseFloat(form.contract_value) : undefined,
        start_date: form.start_date || undefined,
        target_end_date: form.target_end_date || undefined,
        priority: form.priority,
        board_id: form.board_id || selectedBoardId || undefined,
      };
      const res = await projectsAPI.create(payload);
      toast.success(`Project ${res.data.job_number} created`);
      setDrawerOpen(false);
      setForm({ job_name: '', customer_id: '', description: '', project_stage: '', contract_value: '', start_date: '', target_end_date: '', priority: 'normal', board_id: '' });
      navigate(`/jobs/projects/${res.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create project');
    } finally { setSaving(false); }
  };

  const handleStagesChanged = () => {
    fetchStages();
    fetchProjects();
    fetchBoards();
  };

  // ---- Board CRUD ----
  const handleCreateBoard = async (name) => {
    try {
      const res = await projectBoardsAPI.create({ name });
      toast.success(`Board "${name}" created`);
      await fetchBoards();
      handleSelectBoard(res.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create board');
    }
  };

  const handleRenameBoard = async (boardId, newName) => {
    try {
      await projectBoardsAPI.update(boardId, { name: newName });
      toast.success('Board renamed');
      fetchBoards();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rename board');
    }
  };

  const handleDeleteBoard = async (boardId) => {
    try {
      await projectBoardsAPI.delete(boardId);
      toast.success('Board deleted');
      await fetchBoards();
      // Switch to default board
      const defaultBoard = boards.find(b => b.is_default && b.id !== boardId);
      if (defaultBoard) handleSelectBoard(defaultBoard.id);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to delete board';
      toast.error(msg);
      throw e; // re-throw so the dialog can show the error
    }
  };

  // ---- Add stage ----
  const handleAddStage = async () => {
    if (!newStageName.trim()) return;
    setSavingNewStage(true);
    try {
      await projectStagesAPI.create({ name: newStageName.trim(), color: newStageColor, board_id: selectedBoardId });
      setNewStageName('');
      setNewStageColor('#94a3b8');
      setAddingStage(false);
      handleStagesChanged();
      toast.success('Stage added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add stage');
    } finally { setSavingNewStage(false); }
  };

  // Group projects by stage for kanban
  const grouped = {};
  stages.forEach(s => { grouped[s.slug] = []; });
  projects.forEach(p => {
    const s = p.project_stage || 'lead';
    if (grouped[s]) grouped[s].push(p);
    else {
      const firstSlug = stages[0]?.slug;
      if (firstSlug && grouped[firstSlug]) grouped[firstSlug].push(p);
    }
  });

  return (
    <div className="space-y-4" data-testid="projects-list">
      {/* Toolbar */}
      <div className="space-y-2">
        {/* Mobile: Search on its own row */}
        <div className="md:hidden">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search projects..." value={search} onChange={e => handleSearch(e.target.value)} className="pl-10 h-9" data-testid="project-search-input-mobile" />
          </div>
        </div>

        {/* Mobile: Board selector + View toggle row */}
        <div className="flex md:hidden items-center justify-between gap-2">
          {boardsLoading ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border bg-background text-sm text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading...
            </div>
          ) : (
            <BoardSelector
              boards={boards}
              selectedBoardId={selectedBoardId}
              onSelectBoard={handleSelectBoard}
              onCreateBoard={handleCreateBoard}
              onRenameBoard={handleRenameBoard}
              onDeleteBoard={handleDeleteBoard}
              canEdit={canEdit}
            />
          )}
          <div className="flex border rounded-md overflow-hidden shrink-0" data-testid="project-view-toggle-mobile">
            <button onClick={() => setViewMode('board')} className={`px-2.5 py-1.5 text-sm ${viewMode === 'board' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`} data-testid="project-view-board">
              <FolderKanban className="w-4 h-4" />
            </button>
            <button onClick={() => setViewMode('list')} className={`px-2.5 py-1.5 text-sm ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`} data-testid="project-view-list">
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Desktop: All controls in one row */}
        <div className="hidden md:flex items-center gap-2">
          {boardsLoading ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border bg-background text-sm text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading boards...
            </div>
          ) : (
            <BoardSelector
              boards={boards}
              selectedBoardId={selectedBoardId}
              onSelectBoard={handleSelectBoard}
              onCreateBoard={handleCreateBoard}
              onRenameBoard={handleRenameBoard}
              onDeleteBoard={handleDeleteBoard}
              canEdit={canEdit}
            />
          )}

          <div className="relative flex-1 min-w-[120px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search projects..." value={search} onChange={e => handleSearch(e.target.value)} className="pl-10 h-9" data-testid="project-search-input" />
          </div>

          <Select value={stageFilter || 'all'} onValueChange={handleStageFilter}>
            <SelectTrigger className="w-[140px] h-9" data-testid="project-stage-filter">
              <SelectValue placeholder="All stages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {stages.map(s => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="flex border rounded-md overflow-hidden shrink-0" data-testid="project-view-toggle">
            <button onClick={() => setViewMode('board')} className={`px-2.5 py-1.5 text-sm ${viewMode === 'board' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`} data-testid="project-view-board">
              <FolderKanban className="w-4 h-4" />
            </button>
            <button onClick={() => setViewMode('list')} className={`px-2.5 py-1.5 text-sm ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`} data-testid="project-view-list">
              <List className="w-4 h-4" />
            </button>
          </div>

          {canEdit && !addingStage && (
            <>
              <Button variant="outline" size="sm" className="h-9" onClick={() => setCardLayoutOpen(true)} data-testid="card-layout-btn" title="Customize card fields">
                <Settings2 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Card Layout</span>
              </Button>
              <Button variant="outline" size="sm" className="h-9" onClick={() => setAddingStage(true)} data-testid="add-stage-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Stage
              </Button>
              <Button onClick={() => setDrawerOpen(true)} data-testid="new-project-btn" className="h-9">
                <Plus className="w-4 h-4 mr-1" /> Project
              </Button>
            </>
          )}
        </div>

        {/* Add stage inline — visible on all screens when active */}
        {canEdit && addingStage && (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="add-stage-inline">
            <Input
              value={newStageName}
              onChange={e => setNewStageName(e.target.value)}
              placeholder="Stage name"
              className="h-9 w-36 text-sm"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleAddStage(); if (e.key === 'Escape') { setAddingStage(false); setNewStageName(''); } }}
              data-testid="new-stage-name-input"
            />
            <div className="flex gap-0.5">
              {PRESET_COLORS.slice(0, 6).map(c => (
                <button key={c} onClick={() => setNewStageColor(c)} className={`w-5 h-5 rounded-full border-2 transition-transform ${newStageColor === c ? 'border-foreground scale-110' : 'border-transparent hover:scale-105'}`} style={{ backgroundColor: c }} />
              ))}
            </div>
            <Button size="sm" className="h-9 text-xs" onClick={handleAddStage} disabled={savingNewStage || !newStageName.trim()} data-testid="confirm-add-stage">Add</Button>
            <Button size="sm" variant="ghost" className="h-9 text-xs px-2" onClick={() => { setAddingStage(false); setNewStageName(''); setNewStageColor('#94a3b8'); }}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : projects.length === 0 && stages.length === 0 && !stageFilter && !search ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="projects-empty">
          <FolderKanban className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-lg font-medium">No stages yet</p>
          <p className="text-sm mt-1">Add a stage to start building your board.</p>
        </div>
      ) : viewMode === 'board' ? (
        <KanbanBoard grouped={grouped} stages={stages} stageConfig={stageConfig} stageFilter={stageFilter}
          navigate={navigate} canEdit={canEdit} onStagesChanged={handleStagesChanged} boardId={selectedBoardId}
          cardLayout={boards.find(b => b.id === selectedBoardId)?.card_layout}
          onOpenCardConfig={() => setCardLayoutOpen(true)} />
      ) : (
        <div className="border rounded-lg overflow-x-auto" data-testid="projects-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-muted-foreground text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-3">Project</th>
                <th className="text-left p-3">Stage</th>
                <th className="text-left p-3">Customer</th>
                <th className="text-right p-3">Contract</th>
                <th className="text-left p-3 min-w-[140px]">Budget Breakdown</th>
                <th className="text-center p-3">Tasks</th>
                <th className="text-left p-3">Start</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {projects.map(p => (
                <tr key={p.id} onClick={() => navigate(`/jobs/projects/${p.id}`)} className="hover:bg-muted/20 cursor-pointer" data-testid={`project-row-${p.id}`}>
                  <td className="p-3">
                    <p className="font-semibold">{p.job_name}</p>
                    <p className="text-xs text-muted-foreground">{p.job_number}</p>
                  </td>
                  <td className="p-3"><StageBadge stage={p.project_stage} stageConfig={stageConfig} /></td>
                  <td className="p-3 text-muted-foreground">{p.customer_name || '\u2014'}</td>
                  <td className="p-3 text-right tabular-nums">{p.contract_value != null ? formatCurrency(p.contract_value) : '\u2014'}</td>
                  <td className="p-3"><CostBreakdownBar breakdown={p.cost_breakdown} variant="row" /></td>
                  <td className="p-3 text-center">{p.task_count > 0 ? `${p.task_done}/${p.task_count}` : '\u2014'}</td>
                  <td className="p-3 text-muted-foreground">{p.start_date || '\u2014'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ListPagination total={total} page={page} pageSize={pageSize}
        onPageChange={(newPage) => {
          const p = new URLSearchParams(searchParams);
          p.set('ppage', newPage.toString());
          setSearchParams(p, { replace: true });
        }} />

      {/* Create Project Drawer */}
      <AppDrawer open={drawerOpen} onOpenChange={setDrawerOpen} title="New Project" description="Create a new project">
        <div className="space-y-4 p-4" data-testid="new-project-form">
          <div><Label>Project Name *</Label><Input value={form.job_name} onChange={e => setForm(f => ({...f, job_name: e.target.value}))} data-testid="new-project-name" /></div>
          <div>
            <Label>Customer</Label>
            <CustomerCombobox
              value={form.customer_id}
              onChange={v => setForm(f => ({...f, customer_id: v}))}
              data-testid="new-project-customer"
            />
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} data-testid="new-project-desc" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Board</Label>
              <Select value={form.board_id || '_none'} onValueChange={v => setForm(f => ({...f, board_id: v === '_none' ? '' : v, project_stage: ''}))}>
                <SelectTrigger data-testid="new-project-board"><SelectValue placeholder="Select board..." /></SelectTrigger>
                <SelectContent>
                  {boards.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stage</Label>
              <Select value={form.project_stage || '_none'} onValueChange={v => setForm(f => ({...f, project_stage: v === '_none' ? '' : v}))} disabled={!form.board_id || formStages.length === 0}>
                <SelectTrigger data-testid="new-project-stage"><SelectValue placeholder={!form.board_id ? 'Select board first' : formStages.length === 0 ? 'No stages' : 'Select stage...'} /></SelectTrigger>
                <SelectContent>
                  {formStages.map(s => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Contract Value</Label><Input type="number" value={form.contract_value} onChange={e => setForm(f => ({...f, contract_value: e.target.value}))} data-testid="new-project-contract" /></div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={v => setForm(f => ({...f, priority: v}))}>
                <SelectTrigger data-testid="new-project-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm(f => ({...f, start_date: e.target.value}))} data-testid="new-project-start" /></div>
            <div><Label>Target End Date</Label><Input type="date" value={form.target_end_date} onChange={e => setForm(f => ({...f, target_end_date: e.target.value}))} data-testid="new-project-end" /></div>
          </div>
          <Button onClick={handleCreate} disabled={saving} className="w-full" data-testid="create-project-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Create Project
          </Button>
        </div>
      </AppDrawer>

      <CardLayoutConfig
        open={cardLayoutOpen}
        onOpenChange={setCardLayoutOpen}
        board={boards.find(b => b.id === selectedBoardId)}
        onSaved={() => fetchBoards()}
      />
    </div>
  );
});
const STAGE_CONFIG = FALLBACK_STAGE_CONFIG;
const STAGE_ORDER = ['lead', 'proposal', 'awarded', 'in_progress', 'punch_list', 'completed', 'closed'];
export { STAGE_CONFIG, STAGE_ORDER, StageBadge, formatCurrency };
